import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Key, Copy, Trash2, Radio } from "lucide-react";
import { toast } from "sonner";

const SCOPES = [
  "read:agents", "chat:agents", "read:crm", "write:crm",
  "read:audit", "read:knowledge", "write:knowledge",
  "read:workflows", "run:workflows",
];

export default function ApiKeys() {
  const [items, setItems] = useState([]);
  const [webhook, setWebhook] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState(["read:agents", "chat:agents"]);
  const [created, setCreated] = useState(null);

  const load = () => {
    api.get("/api-keys").then((r) => setItems(r.data.api_keys));
    api.get("/webhooks/config").then((r) => setWebhook(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!label) return toast.error("Etiqueta requerida");
    try {
      const { data } = await api.post("/api-keys", { label, scopes });
      setCreated(data);
      toast.success("API Key creada");
      setLabel(""); setScopes(["read:agents", "chat:agents"]);
      setDlg(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fallo"); }
  };

  const revoke = async (kid) => {
    if (!window.confirm("¿Revocar esta key? No podrá recuperarse.")) return;
    await api.delete(`/api-keys/${kid}`);
    toast.success("Revocada");
    load();
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };

  const toggleScope = (s) => {
    setScopes(scopes.includes(s) ? scopes.filter((x) => x !== s) : [...scopes, s]);
  };

  const BE = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="p-8 space-y-6" data-testid="apikeys-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">ACCESO PROGRAMÁTICO</div>
          <h1 className="font-display font-black text-4xl tracking-tight">API Keys & Webhooks</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Integra Agent Company OS con tus sistemas: llama al API o dispara eventos desde fuera.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="ak-add">
              <Key className="w-4 h-4 mr-2" /> Nueva API Key
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <DialogHeader><DialogTitle className="font-display">Nueva API Key</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Etiqueta</Label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)}
                       placeholder="CI/CD pipeline · Zapier · etc."
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="ak-label" />
              </div>
              <div>
                <Label className="label-mono">Scopes</Label>
                <div className="grid grid-cols-2 gap-1 mt-2">
                  {SCOPES.map((s) => (
                    <button key={s} onClick={() => toggleScope(s)}
                            className={`text-xs px-2 py-1.5 border font-mono text-left ${
                              scopes.includes(s) ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]"
                                                 : "border-[var(--border)] text-[var(--text-dim)]"
                            }`}
                            data-testid={`ak-scope-${s.replace(":", "-")}`}>
                      {scopes.includes(s) ? "✓" : "○"} {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="ak-submit">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {created && (
        <div className="border border-[var(--warn)] bg-[var(--warn)]/5 p-4" data-testid="ak-created">
          <div className="label-mono mb-2" style={{ color: "var(--warn)" }}>COPIA ESTA KEY AHORA — no se muestra otra vez</div>
          <div className="flex items-center gap-3">
            <code className="font-mono text-sm text-white bg-[var(--bg)] px-3 py-2 border border-[var(--border)] flex-1 break-all">
              {created.key}
            </code>
            <Button onClick={() => copy(created.key)} variant="outline" className="rounded-none">
              <Copy className="w-3 h-3 mr-2" /> Copiar
            </Button>
            <button onClick={() => setCreated(null)} className="label-mono">✕</button>
          </div>
        </div>
      )}

      <div className="border border-[var(--border)] bg-[var(--surface)]">
        <div className="p-4 border-b border-[var(--border)] label-mono">API KEYS ACTIVAS</div>
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--text-dim)] font-mono">Sin API keys aún.</div>
        ) : items.map((k) => (
          <div key={k.id} className="p-4 border-b border-[var(--border)] flex items-center gap-4" data-testid={`ak-${k.id}`}>
            <Key className={`w-4 h-4 ${k.revoked ? "text-[var(--text-dim)]" : "text-[var(--accent)]"}`} />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">{k.label}</div>
              <div className="flex items-center gap-2 mt-1">
                <code className="font-mono text-[10px] text-[var(--text-dim)]">{k.prefix}…</code>
                {k.scopes?.map((s) => (
                  <Badge key={s} className="rounded-none font-mono text-[9px] bg-[var(--surface-2)] border border-[var(--border)] text-white hover:bg-[var(--surface-2)]">
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
            {k.revoked ? (
              <Badge className="rounded-none font-mono text-[10px] bg-[var(--danger)] text-[var(--bg)] hover:bg-[var(--danger)]">
                REVOCADA
              </Badge>
            ) : (
              <button onClick={() => revoke(k.id)} className="text-[var(--text-dim)] hover:text-[var(--danger)]" data-testid={`ak-revoke-${k.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Radio className="w-4 h-4 text-[var(--accent)]" />
          <div className="label-mono">WEBHOOK ENTRANTE</div>
        </div>
        <p className="text-xs text-[var(--text-dim)] mb-3">
          POST a esta URL para disparar eventos en tu tenant desde herramientas externas (Zapier, Stripe, WhatsApp, etc.).
        </p>
        {webhook && (
          <div className="flex items-center gap-2 mb-3">
            <code className="font-mono text-xs text-white bg-[var(--bg)] px-3 py-2 border border-[var(--border)] flex-1 break-all" data-testid="wh-url">
              POST {BE}{webhook.endpoint}
            </code>
            <Button onClick={() => copy(BE + webhook.endpoint)} variant="outline" className="rounded-none">
              <Copy className="w-3 h-3 mr-2" /> Copiar
            </Button>
          </div>
        )}
        <div className="text-xs text-[var(--text-dim)] font-mono">
          Body: {`{"source":"zapier","event":"lead.created","payload":{...}}`}
        </div>

        {webhook?.recent_events?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            <div className="label-mono mb-2">EVENTOS RECIENTES</div>
            <div className="space-y-1">
              {webhook.recent_events.slice(0, 5).map((e) => (
                <div key={e.id} className="font-mono text-[10px] text-[var(--text-dim)]" data-testid={`wh-ev-${e.id}`}>
                  <span className="text-[var(--accent)]">{e.source}</span> · {e.event} ·{" "}
                  {new Date(e.timestamp).toLocaleString("es-ES")}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
